/**
 * Shared scoring rubric constants and helpers for the 4-category / 20-dimension scoring system.
 */

export interface DimensionDef {
  field: string;
  label: string;
}

export interface CategoryDef {
  key: string;
  label: string;
  scoreField: string;
  dimensions: DimensionDef[];
}

export const SCORING_RUBRIC: CategoryDef[] = [
  {
    key: 'engagement',
    label: 'Engagement',
    scoreField: 'engagement_score',
    dimensions: [
      { field: 'positive_tone_score', label: 'Positive Tone' },
      { field: 'professional_score', label: 'Professionalism' },
      { field: 'clear_objective_score', label: 'Clear Objective' },
      { field: 'agenda_set_score', label: 'Agenda Setting' },
      { field: 'relationship_level_score', label: 'Relationship Building' },
    ],
  },
  {
    key: 'discovery',
    label: 'Discovery',
    scoreField: 'discovery_score',
    dimensions: [
      { field: 'questions_variety_score', label: 'Question Variety' },
      { field: 'bant_qualification_score', label: 'BANT Qualification' },
      { field: 'persona_wisdom_score', label: 'Persona & Wisdom' },
      { field: 'clients_success_defined_score', label: 'Client Success Defined' },
      { field: 'listen_talk_ratio_score', label: 'Listen/Talk Ratio' },
    ],
  },
  {
    key: 'presentation',
    label: 'Presentation',
    scoreField: 'presentation_score',
    dimensions: [
      { field: 'value_articulation_score', label: 'Value Articulation' },
      { field: 'sme_expertise_score', label: 'SME Expertise' },
      { field: 'storytelling_score', label: 'Storytelling' },
      { field: 'third_party_examples_score', label: 'Third-Party Examples' },
      { field: 'tie_to_outcomes_score', label: 'Tie to Outcomes' },
      { field: 'communication_clear_score', label: 'Communication Clarity' },
    ],
  },
  {
    key: 'closing',
    label: 'Closing',
    scoreField: 'closing_score',
    dimensions: [
      { field: 'objection_handling_score', label: 'Objection Handling' },
      { field: 'next_steps_timeline_score', label: 'Next Steps & Timeline' },
      { field: 'gained_commitment_score', label: 'Gained Commitment' },
      { field: 'rep_owns_ball_score', label: 'Rep Owns the Ball' },
    ],
  },
];

/**
 * Return a MUI-compatible color for a 0-5 score.
 * 🟥 0-2: error (red), 🟨 3: warning (yellow), 🟩 4-5: success (green)
 */
export function getScoreColor(
  score: number | null
): 'error' | 'warning' | 'success' | 'inherit' {
  if (score == null) return 'inherit';
  if (score <= 2) return 'error';
  if (score <= 3) return 'warning';
  return 'success';
}

/**
 * Level-Up Framework tier info for an overall score (0-100).
 */
export interface TierInfo {
  emoji: string;
  name: string;
  description: string;
  color: 'error' | 'warning' | 'info' | 'success';
}

export function getTier(overallScore: number): TierInfo {
  if (overallScore >= 93)
    return {
      emoji: '🏆',
      name: 'Elite — The 7% Club',
      description: 'Leading with confidence. Keep sharpening.',
      color: 'success',
    };
  if (overallScore >= 75)
    return {
      emoji: '🚀',
      name: 'Advanced — Strategic Performer',
      description: 'Strong performance. Small shifts to elevate.',
      color: 'info',
    };
  if (overallScore >= 61)
    return {
      emoji: '⚙️',
      name: 'Competent — Solid Foundation',
      description: "You've got traction. Push to break through.",
      color: 'warning',
    };
  return {
    emoji: '🔧',
    name: 'Developing — Build & Refine',
    description: 'Every rep is practice. Always the apprentice.',
    color: 'error',
  };
}
